import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { loadYaml } from "../utils/loadYaml";
import {
  FiSearch,
  FiUsers,
  FiGlobe,
  FiShield,
  FiLayers,
  FiActivity,
  FiDatabase,
  FiRadio,
  FiChevronDown,
  FiGrid,
  FiCreditCard,
  FiCloud,
} from "react-icons/fi";
import { useTranslation } from "react-i18next";
import "./navbar.css";
import ProductSuiteIcon from "./ProductSuiteIcon";
import ProductLauncher from "./ProductLauncher";

interface MinorItem { title: string; link?: string; subMinor?: MinorItem[] }
interface MajorItem { link?: string; major: string; minor?: MinorItem[] }
interface ProductItem { icon?: string; title: string; description: string; link?: string }
interface SidePanel { title?: string; items?: { title: string; link?: string }[] }
interface MenuItem { title: string; link?: string; dropdown?: MajorItem[]; layout?: "product"; products?: ProductItem[]; sidePanel?: SidePanel }
interface IconItem { type: "search" | "i18n"; dropdown?: any }
interface NavbarData { brand: { logo: string; name: string; link: string }; menu: MenuItem[]; icons?: IconItem[] }

const iconMap: Record<string, any> = { search: FiSearch, i18n: FiGlobe, chevron: FiChevronDown };
const getIcon = (type: string, props: any = {}) => React.createElement(iconMap[type], { size: 20, ...props });
const langMap: Record<string, string> = { "EN-GB": "en-GB", HI: "hi", FR: "fr", DE: "de" };

const Navbar: React.FC = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [data, setData] = useState<NavbarData | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [hoveredMajor, setHoveredMajor] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const [dropdownPositions, setDropdownPositions] = useState<Record<number, "left" | "right">>({});
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [recentSearches, setRecentSearches] = useState<string[]>([]);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [openMobileDropdown, setOpenMobileDropdown] = useState<number | null>(null);
  const [openMajorDropdown, setOpenMajorDropdown] = useState<number | null>(null);
  const [iconDropdownState, setIconDropdownState] = useState<{ [key: string]: boolean }>({ i18n: false });
  const [isProductLauncherOpen, setIsProductLauncherOpen] = useState(false);
  const [launcherDropdownStyle, setLauncherDropdownStyle] = useState<React.CSSProperties>({});

  const menuRefs = useRef<(HTMLLIElement | null)[]>([]);
  const searchRef = useRef<HTMLDivElement | null>(null);
  const navRef = useRef<HTMLElement | null>(null);
  const launcherRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    loadYaml("/config/navbar.yaml")
      .then((result) => {
        if (result) {
          setData(result);
        } else {
          setLoadError("Navbar content could not be loaded.");
        }
      })
      .catch(() => setLoadError("Navbar content could not be loaded."));
  }, []);

  useEffect(() => {
    const stored = localStorage.getItem("recentSearches");
    if (stored) setRecentSearches(JSON.parse(stored));
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSearchOpen && searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsSearchOpen(false); setHighlightedIndex(-1);
      }
      if (activeDropdown !== null && navRef.current && !navRef.current.contains(event.target as Node)) {
        setActiveDropdown(null);
      }
      Object.keys(iconDropdownState).forEach(key => {
        const dropdowns = document.querySelectorAll(".icon-dropdown");
        let clickedInside = false;
        dropdowns.forEach(d => { if (d.contains(event.target as Node)) clickedInside = true; });
        if (!clickedInside) setIconDropdownState(prev => ({ ...prev, [key]: false }));
      });
      if (isProductLauncherOpen && launcherRef.current && !launcherRef.current.contains(event.target as Node)) {
        setIsProductLauncherOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSearchOpen, iconDropdownState, activeDropdown, isProductLauncherOpen]);

  useEffect(() => {
    if (!isProductLauncherOpen) return;

    const updateLauncherPosition = () => {
      if (!launcherRef.current) return;
      const rect = launcherRef.current.getBoundingClientRect();
      const availableRight = window.innerWidth - rect.right - 16;
      const availableLeft = rect.left - 16;
      const preferredWidth = window.innerWidth < 1100 ? 420 : 520;
      const fallbackWidth = availableRight < 420 && availableLeft < 420 ? 360 : preferredWidth;
      const compactWidth = Math.min(fallbackWidth, Math.max(320, availableRight > 260 ? availableRight : availableLeft));
      const panelWidth = Math.min(preferredWidth, Math.max(320, compactWidth));
      const alignedLeft = rect.left + rect.width / 2 - panelWidth / 2;
      const clampedLeft = Math.max(8, Math.min(alignedLeft, window.innerWidth - panelWidth - 8));
      setLauncherDropdownStyle({
        position: "fixed",
        top: rect.bottom + 12,
        left: clampedLeft,
        width: panelWidth,
      });
    };

    updateLauncherPosition();
    window.addEventListener("resize", updateLauncherPosition);
    window.addEventListener("scroll", updateLauncherPosition, true);
    return () => {
      window.removeEventListener("resize", updateLauncherPosition);
      window.removeEventListener("scroll", updateLauncherPosition, true);
    };
  }, [isProductLauncherOpen]);

  if (!data) {
    return (
      <nav className="navbar navbar-loading" ref={navRef}>
        <div className="navbar-left">
          <span className="navbar-loading-text">
            {loadError || "Loading navigation..."}
          </span>
        </div>
      </nav>
    );
  }

  const handleDropdownEnter = (idx: number) => {
    const menuItem = menuRefs.current[idx];
    if (menuItem) {
      const rect = menuItem.getBoundingClientRect();
      const spaceRight = window.innerWidth - rect.left;
      const position: "left" | "right" = spaceRight < 500 ? "right" : "left";
      setDropdownPositions(prev => ({ ...prev, [idx]: position }));
    }
    setActiveDropdown(idx);
  };

  const handleDropdownLeave = () => { setActiveDropdown(null); setHoveredMajor(null); };
  const handleDropdownClick = (idx: number, hasDropdown: boolean) => {
    if (!hasDropdown) return;
    setActiveDropdown(prev => (prev === idx ? null : idx));
    if (hoveredMajor === null) setHoveredMajor(0);
  };
  const isMenuActive = (item: MenuItem) => {
    const path = location.pathname.toLowerCase();
    const title = item.title.toLowerCase();

    if (title === "products") return path.startsWith("/products");
    if (title === "industries") return path === "/industries";
    if (title === "company") return path === "/" || path === "/company";
    return item.link ? path === item.link.toLowerCase() : false;
  };
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((current) => {
      const next = !current;

      if (next) {
        const activeMenuIndex = data.menu.findIndex(isMenuActive);
        setOpenMobileDropdown(activeMenuIndex >= 0 ? activeMenuIndex : 0);
        setOpenMajorDropdown(null);
        setActiveDropdown(null);
        setIsProductLauncherOpen(false);
      }

      return next;
    });
  };
  const closeMobileMenu = () => setIsMobileMenuOpen(false);
  const toggleIcon = (type: "i18n") => { setIconDropdownState(prev => ({ ...prev, [type]: !prev[type] })); setIsSearchOpen(false); };
  const toggleSearch = () => { setIsSearchOpen(true); setIconDropdownState({ i18n: false }); setTimeout(() => { searchRef.current?.querySelector<HTMLInputElement>("input")?.focus(); }, 0); };
  const changeLanguage = (label: string) => { const lng = langMap[label]; if (lng) { i18n.changeLanguage(lng); setIconDropdownState({ i18n: false }); } };
  const tMinor = (key: string) => t(`minor.${key}`, { defaultValue: key });
  const tMenu = (key: string) => t(`menu.${key}`, { defaultValue: key.charAt(0).toUpperCase() + key.slice(1) });
  const getLauncherIcon = (icon?: string) => {
    const iconLookup: Record<string, React.ReactNode> = {
      FiUsers: <FiUsers size={22} />,
      FiGrid: <FiGrid size={22} />,
      FiGlobe: <FiGlobe size={22} />,
      FiCreditCard: <FiCreditCard size={22} />,
      FiCloud: <FiCloud size={22} />,
      FiCpu: <FiLayers size={22} />,
      FiActivity: <FiActivity size={22} />,
      FiShield: <FiShield size={22} />,
      FiLayers: <FiLayers size={22} />,
      FiRadio: <FiRadio size={22} />,
      FiDatabase: <FiDatabase size={22} />,
    };
    return iconLookup[icon || ""] ?? <FiGrid size={22} />;
  };

  const filteredRecent = recentSearches.filter(item =>
    searchTerm === "" || item.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const submitSearch = (term: string) => {
    setSearchTerm(term);
    setIsSearchOpen(false);
    setHighlightedIndex(-1);
    if (!term) return;
    const updated = [term, ...recentSearches.filter(t => t !== term)].slice(0, 5);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isSearchOpen || filteredRecent.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlightedIndex(prev => (prev + 1) % filteredRecent.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlightedIndex(prev => (prev - 1 + filteredRecent.length) % filteredRecent.length); }
    else if (e.key === "Enter") { e.preventDefault(); if (highlightedIndex >= 0 && highlightedIndex < filteredRecent.length) submitSearch(filteredRecent[highlightedIndex]); }
  };

  return (
    <nav className="navbar" ref={navRef}>
      {isProductLauncherOpen && (
        <div
          className="launcher-backdrop"
          aria-hidden="true"
          onClick={() => setIsProductLauncherOpen(false)}
        />
      )}
      {activeDropdown !== null && (
        <div className="dropdown-backdrop" aria-hidden="true" onClick={() => setActiveDropdown(null)}></div>
      )}
      {isMobileMenuOpen && (
        <div className="hamburger-backdrop" aria-hidden="true" onClick={closeMobileMenu}></div>
      )}
      {/* Left: Brand + Hamburger */}
      <div className="navbar-left">
        <div className="navbar-brand">
          <a href={data.brand.link} className="brand-link">
            <img src={data.brand.logo} alt="Brand" className="brand-logo" />
             </a>
        </div>

        {/* Hamburger Mobile Menu */}
        <div className="hamburger-wrapper mobile-only">
          <button
            type="button"
            className={`burger ${isMobileMenuOpen ? "open" : ""}`}
            onClick={toggleMobileMenu}
            aria-haspopup="menu"
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <span className="burger-line burger-line-top" />
            <span className="burger-line burger-line-middle" />
            <span className="burger-line burger-line-bottom" />
          </button>

          {isMobileMenuOpen && (
            <div className="hamburger-dropdown">
              <div className="mobile-panels-container">
                {/* Left Panel */}
                <div className="mobile-left-panel">
                  {data.menu.map((item, topIdx) => (
                    <div
                      key={topIdx}
                      className={`mobile-left-item ${openMobileDropdown === topIdx ? "active" : ""}`}
                      onClick={() =>
                        setOpenMobileDropdown(openMobileDropdown === topIdx ? null : topIdx)
                      }
                    >
                      {tMenu(item.title)}
                      <span>›</span>
                    </div>
                  ))}
                </div>

                {/* Right Panel */}
                {openMobileDropdown !== null && (
                  <div className="mobile-right-panel">
                    {data.menu[openMobileDropdown]?.layout === "product" ? (
                      <div className="mobile-product-panel">
                        {data.menu[openMobileDropdown]?.products?.map((product, pIdx) => (
                          <a key={pIdx} className="mobile-product-item" href={product.link || "#"} onClick={closeMobileMenu}>
                            <span className="mobile-product-title">{product.title}</span>
                            <span className="mobile-product-desc">{product.description}</span>
                          </a>
                        ))}
                        {data.menu[openMobileDropdown]?.sidePanel?.title && (
                          <div className="mobile-side-title">{data.menu[openMobileDropdown]?.sidePanel?.title}</div>
                        )}
                        {data.menu[openMobileDropdown]?.sidePanel?.items?.map((item, sIdx) => (
                          <a key={sIdx} className="mobile-side-item" href={item.link || "#"} onClick={closeMobileMenu}>
                            {item.title}
                          </a>
                        ))}
                      </div>
                    ) : (
                      data.menu[openMobileDropdown]?.dropdown?.map((major, mIdx) => (
                        <div key={mIdx} className="mobile-major-block">
                          <div
                            className={`mobile-major ${openMajorDropdown === mIdx ? "active" : ""}`}
                            onClick={() =>
                              setOpenMajorDropdown(openMajorDropdown === mIdx ? null : mIdx)
                            }
                          >
                            {tMenu(major.major)}
                            {major.minor && major.minor.length > 0 && (
                              <span
                                className={`icon-arrow ${openMajorDropdown === mIdx ? "rotated" : ""}`}
                              >
                                ▼
                              </span>
                            )}
                          </div>

                          {openMajorDropdown === mIdx && major.minor && (
                            <div className="mobile-minor-list">
                              {major.minor.map((minor, miIdx) => (
                                <div key={miIdx} className="mobile-minor-item">
                                  <a href={minor.link || "#"} onClick={closeMobileMenu}>{tMinor(minor.title)}</a>
                                  {minor.subMinor && minor.subMinor.length > 0 && (
                                    <div className="mobile-subminor-list">
                                      {minor.subMinor.map((sub, sIdx) => (
                                        <a key={sIdx} href={sub.link || "#"} onClick={closeMobileMenu}>{tMinor(sub.title)}</a>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop menu */}
      <ul className="navbar-menu desktop-only">
        {data.menu.map((item, idx) => (
          <li key={idx} className={`navbar-item ${isMenuActive(item) ? "navbar-item-active" : ""}`} ref={el => { menuRefs.current[idx] = el || null; }}
              onClick={() => handleDropdownClick(idx, !!(item.dropdown?.length || item.layout === "product"))}>
            {item.link ? <a href={item.link}>{tMenu(item.title)}</a> : <span>{tMenu(item.title)}</span>}
            {activeDropdown === idx && <span className="nav-caret" />}
            {item.dropdown && item.dropdown.length > 0 && activeDropdown === idx && (
              <div className={`dropdown-wrapper ${dropdownPositions[idx]==="right"?"dropdown-right-align":""}`}>
                <div className="dropdown">
                  <div className="dropdown-left">
                    {item.dropdown.map((block,i)=>(
                      <a key={i} href={block.link||"#"} className={`major-title ${hoveredMajor===i?"active":""}`} onMouseEnter={()=>setHoveredMajor(i)}>
                        {tMenu(block.major)}
                      </a>
                    ))}
                  </div>
                  <div className="dropdown-right">
                    {hoveredMajor!==null && item.dropdown[hoveredMajor]?.minor?.map((minor,i)=>(
                      <a key={i} href={minor.link||"#"} className="minor-link">{tMinor(minor.title)}</a>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {item.layout === "product" && activeDropdown === idx && (
              <div className={`dropdown-wrapper dropdown-wide ${dropdownPositions[idx]==="right"?"dropdown-right-align":""}`}>
                <div className="dropdown-surface">
                  <ProductLauncher
                    variant="mega"
                    items={(item.products || []).map((product) => ({
                      title: product.title,
                      description: product.description,
                      route: product.link || "#",
                      accentClass: "accent-blue",
                      icon: getLauncherIcon(product.icon),
                    }))}
                  />
                </div>
              </div>
            )}
          </li>
        ))}
      </ul>

      {/* Right Icons */}
      <div className="navbar-icons">
        {/* Search */}
        

       

        {/* Language */}
        <div className="navbar-icon-wrapper">
        {/* Product launcher */}
        <div className="navbar-launcher-group" ref={launcherRef}>
          <button
            type="button"
            className="navbar-launcher-button"
            aria-haspopup="menu"
            aria-expanded={isProductLauncherOpen}
            onClick={() => setIsProductLauncherOpen((prev) => !prev)}
          >
            <ProductSuiteIcon />
          </button>
          {isProductLauncherOpen && (
            <div className="navbar-launcher-dropdown" role="menu" style={launcherDropdownStyle}>
              <ProductLauncher />
            </div>
          )}
        </div>

        <div className="navbar-icon clickable" onClick={()=>toggleIcon("i18n")}>
            {getIcon("i18n")}<span className="active-lang">EN</span>
            
          </div>

           
        
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
